import { takeEvery, put, call } from 'redux-saga/effects';
import { models } from 'powerbi-client';

import * as types from './performanceDashboardCombinedActionType';
import * as actions from './performanceDashboardCombinedActions';
import * as services from './performanceDashboardCombinedServices';

function* fetchPerformanceDashboardCombinedPowerBIReportSaga() {
    const environment = window.configuration.ENVIRONMENT || 'dev';
    const sharedReport = window.configuration.PowerBI_Reports?.performanceDashboardCombined?.sharedReport;
    const environmentModelConfig = window.configuration.PowerBI_Reports?.performanceDashboardCombined?.environmentModelConfig?.[environment];

    if (!sharedReport || !environmentModelConfig) {
        console.error('No Power BI report configured for environment:', environment);
        yield put(actions.fetchPerformanceDashboardCombinedPowerBIReportFailure('No Power BI report configured for this environment'));
        return;
    }

    const { reportId, reportWorkspaceId, goldenModelId } = sharedReport;
    const { modelId: targetModelId, modelWorkspaceId: targetModelWorkspaceId } = environmentModelConfig;

    try {
        const response = yield call(services.getPerformanceDashboardCombinedPowerBIToken);

        const embedPayload = {
            token: response?.access_token,
            reportId,
            workspaceId: reportWorkspaceId
        }
        const embedResponse = yield call(services.getPerformanceDashboardCombinedEmbedResponse, embedPayload);
        const tokenResponse = yield call(services.getPerformanceDashboardCombinedMultiResourceTokenResponse, {
            token: response?.access_token,
            reportId,
            goldenDatasetId: goldenModelId,
            datasetId: targetModelId,
            reportWorkspaceId,
            datasetWorkspaceId: targetModelWorkspaceId
        });

        const embedConfig = {
            type: "report",
            id: reportId,
            embedUrl: embedResponse?.embedUrl,
            accessToken: tokenResponse?.token,
            tokenType: models.TokenType.Embed,
            permissions: models.Permissions.Read,
            // Dynamic binding for a thin report: same report id, different bound semantic
            // model per environment. Note the SINGULAR `datasetBinding` shape ({ datasetId }) —
            // this is a distinct property from the plural `datasetBindings` (paginated-report
            // rebinding), which is a silent no-op for a regular report. Confirmed against a
            // working reference implementation.
            datasetBinding: {
                datasetId: targetModelId
            },
            settings: {
                filterPaneEnabled: false,
                navContentPaneEnabled: true
            }
        };

        yield put(actions.fetchPerformanceDashboardCombinedPowerBIReportSuccess(embedConfig));
    } catch (error) {
        console.error('Performance Dashboard Combined PowerBI API Error:', error);
        yield put(actions.fetchPerformanceDashboardCombinedPowerBIReportFailure('Failed to fetch PowerBI report'));
    }
}

function* fetchPerformanceDashboardCombinedLastRefreshedSaga() {
    try {
        const response = yield call(services.getPerformanceDashboardCombinedLastRefreshed);
        const lastRefreshedDateTime = response?.values?.LastRefreshedDateTime;
        const nextRefreshDateTime = response?.values?.NextRefreshDateTime ?? null;

        if (response?.success !== true || response?.values?.ExceptionId || !lastRefreshedDateTime) {
            throw new Error(response?.errorMessage || 'Failed to fetch Performance Dashboard refresh date');
        }

        yield put(actions.fetchPerformanceDashboardCombinedLastRefreshedSuccess(lastRefreshedDateTime, nextRefreshDateTime));
    } catch (error) {
        console.error('Performance Dashboard Combined Last Refreshed API Error:', error);
        yield put(actions.fetchPerformanceDashboardCombinedLastRefreshedFailure('Failed to fetch refresh date'));
    }
}

export default function* performanceDashboardCombinedSaga() {
    yield takeEvery(types.FETCH_PERFORMANCE_DASHBOARD_COMBINED_POWER_BI_REPORT_REQUEST, fetchPerformanceDashboardCombinedPowerBIReportSaga);
    yield takeEvery(types.FETCH_PERFORMANCE_DASHBOARD_COMBINED_LAST_REFRESHED_REQUEST, fetchPerformanceDashboardCombinedLastRefreshedSaga);
}
